/* global JSX */
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { Alert, Box, Stack, Typography, alpha } from "@mui/material";
import * as React from "react";
import { useDropzone } from "react-dropzone";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { AttachmentImageSize, FileType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { UploadButton } from "source/components/framework/Button/UploadButton";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { AttachmentImage } from "./AttachmentImage";
import { AttachmentListLine } from "./AttachmentListLine";

export type AttachmentListDirection = "column" | "row";

interface IAttachmentListProp {
    title: string | JSX.Element;
    imageSize?: AttachmentImageSize;
    accept?: string;
    lateThreshold?: number;
    attachments: IFileAttachment[];
    isReadOnly?: boolean;
    margin?: string | number;
    size?: "sm" | "md",
    hideThumbnails?: boolean;
    direction?: AttachmentListDirection;
    ignoreAspectRatio?: boolean;
    isNewMessage?: string;
    onRemoveAttachment: (id: string) => void;
    onDownloadAttachment: (id: string) => void;
    onAddAttachments: (fileList: FileList) => void;
    onAddPhoto?: () => void;
}

export const AttachmentList = ({
    attachments,
    title,
    accept,
    isReadOnly,
    lateThreshold,
    margin,
    size = "sm",
    imageSize,
    hideThumbnails,
    direction = "column",
    ignoreAspectRatio,
    isNewMessage,
    onAddAttachments,
    onDownloadAttachment,
    onRemoveAttachment,
    onAddPhoto
}: IAttachmentListProp) => {
    const fileUploadRef = React.useRef<HTMLInputElement>(null);

    const onDrop = React.useCallback(acceptedFiles => {
        onAddAttachments(acceptedFiles);
    }, []);

    const { getRootProps, getInputProps } = useDropzone({ noClick: true, onDrop });

    const handleAddFilesClick = (e) => {
        fileUploadRef.current.click();
    };

    const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length < 1) return;
        onAddAttachments(e.target.files);
    };

    const handleAddPhotoClick = () => {
        onAddPhoto();
    };

    const handleDownload = (id: string) => {
        if (!id) return;
        onDownloadAttachment(id);
    };

    const handleRemove = (id: string) => {
        if (!id) return;
        onRemoveAttachment(id);
    };

    const imageAttachments = !hideThumbnails ? attachments.filter(i => i.type === FileType.Image) : [];
    const fileAttachments = !hideThumbnails ? attachments.filter(i => i.type !== FileType.Image) : attachments;

    return (
        <FormField title={title as string} margin={margin}>
            {!isReadOnly && (
                <Box
                    {...getRootProps()}
                    sx={(theme) => ({
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexGrow: 2,
                        textAlign: "center",
                        borderRadius: ".25rem",
                        border: "2px dashed #7197CD",
                        padding: size === "md" ? "2rem 1rem 1.5rem 1rem" : ".5rem",
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            theme.palette.action.hoverOpacity
                        )
                    })}
                >
                    <input {...getInputProps()} ref={fileUploadRef} type="file" name="attachments" className="file" style={{ display: "none" }} multiple={true} accept={accept} onChange={handleAddFiles} />
                    <Typography variant={size === "md" ? "h6" : "body2"} fontWeight={600} sx={{ marginBottom: size === "md" ? "1.5rem" : ".5rem" }}>
                        {onAddPhoto ? Translations.AddFileOrTakePicture : Translations.AddFile}.
                    </Typography>
                    <Box display="flex">
                        <UploadButton variant="outlined" size={size === "sm" ? "small" : "medium"} onClick={handleAddFilesClick}>
                            {Translations.BrowseFiles}
                        </UploadButton>
                        {!!onAddPhoto && (
                            <Box marginLeft="1rem">
                                <AppButton variant="outlined" size={size === "sm" ? "small" : "medium"} onClick={handleAddPhotoClick} startIcon={<PhotoCameraOutlinedIcon />}>
                                    {Translations.TakePicture}
                                </AppButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", overflowY: "auto" }}>
                {!!isNewMessage &&
                    <Alert severity="info" sx={{ marginBottom: "1rem" }}>{isNewMessage}</Alert>
                }
                {imageAttachments.length > 0 &&
                    <Stack direction="row" useFlexGap m={1} sx={{ flexWrap: "wrap", marginTop: ".5rem", }}>
                        {imageAttachments.map((attachment) =>
                            <AttachmentImage
                                key={attachment.id}
                                imageSize={imageSize}
                                lateThreshold={lateThreshold}
                                attachment={attachment}
                                isReadOnly={isReadOnly || attachment.category === 999}
                                // selectedId={state.selectedId}
                                // onClick={handleAttachmentClick}
                                // onDoubleClick={handleAttachmentDoubleClick}
                                ignoreAspectRatio={ignoreAspectRatio}
                                onDownload={handleDownload}
                                onRemove={handleRemove}
                            />
                        )}
                    </Stack>
                }
                <Stack direction={direction} my={1} sx={{ flexWrap: "wrap", margin: direction === "row" && hideThumbnails ? 0 : null }}>
                    {fileAttachments.length > 0
                        ? fileAttachments.map((attachment) =>
                            <AttachmentListLine
                                key={attachment.id}
                                lateThreshold={lateThreshold}
                                attachment={attachment}
                                // selectedId={state.selectedId}
                                isReadOnly={isReadOnly || attachment.category === 999}
                                direction={direction}
                                onDownload={handleDownload}
                                onRemove={handleRemove}
                            />
                        )
                        : ""
                    }
                </Stack>
            </Box>
        </FormField>
    );
};